import React from "react";
import { View, Text, StyleSheet, Pressable } from "../platformComponents";
import { i18nCrossUtil } from "@adgm/libs";

const colors = {
  white: "#FFFFFF",
  whiteOp7: "rgba(255, 255, 255, 0.7)",
  whiteOp8: "rgba(255, 255, 255, 0.8)",
  orange: "#F17E00",
};

const typography = {
  regular: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: "DINPro-Regular",
  },
  medium: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: "DINPro-Medium",
  },
  bold: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: "DINPro-Bold",
  },
};

interface BulletPointProps {
  text: string;
}
const BulletPoint: React.FC<BulletPointProps> = ({ text }) => (
  <View style={styles.bulletPoint}>
    <View style={styles.bullet} />
    <Text style={styles.bulletText}>{text}</Text>
  </View>
);

interface RiskSectionProps {
  title: string;
  description: string;
  bulletPoints?: string[];
  additionalContent?: string;
}
const RiskSection: React.FC<RiskSectionProps> = props => {
  const { title, description, bulletPoints = [], additionalContent } = props;
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.description}>{description}</Text>
      {bulletPoints.map((point, index) => (
        <BulletPoint key={index} text={point} />
      ))}
      {additionalContent && <Text style={styles.additionalContent}>{additionalContent}</Text>}
    </View>
  );
};

interface StakingComplianceProps {
  onAcknowledge?: () => void;
}
export const StakingCompliance: React.FC<StakingComplianceProps> = ({ onAcknowledge }) => {
  const crossInt = i18nCrossUtil.t();

  return (
    <View style={styles.content}>
      <Text style={styles.mainTitle}>{crossInt["staking.compliance.title"]}</Text>
      <Text style={styles.mainDescription}>{crossInt["staking.compliance.description"]}</Text>

      <RiskSection
        title={crossInt["staking.compliance.risk1.title"]}
        description={crossInt["staking.compliance.risk1.description"]}
        bulletPoints={[
          crossInt["staking.compliance.risk1.bullet1"],
          crossInt["staking.compliance.risk1.bullet2"],
          crossInt["staking.compliance.risk1.bullet3"],
        ]}
        additionalContent={crossInt["staking.compliance.risk1.additionalContent"]}
      />

      <RiskSection
        title={crossInt["staking.compliance.risk2.title"]}
        description={crossInt["staking.compliance.risk2.description"]}
        bulletPoints={[
          crossInt["staking.compliance.risk2.bullet1"],
          crossInt["staking.compliance.risk2.bullet2"],
          crossInt["staking.compliance.risk2.bullet3"],
        ]}
      />

      <RiskSection
        title={crossInt["staking.compliance.risk3.title"]}
        description={crossInt["staking.compliance.risk3.description"]}
        bulletPoints={[
          crossInt["staking.compliance.risk3.bullet1"],
          crossInt["staking.compliance.risk3.bullet2"],
        ]}
        additionalContent={crossInt["staking.compliance.risk3.additionalContent"]}
      />

      <RiskSection
        title={crossInt["staking.compliance.risk4.title"]}
        description={crossInt["staking.compliance.risk4.description"]}
        bulletPoints={[
          crossInt["staking.compliance.risk4.bullet1"],
          crossInt["staking.compliance.risk4.bullet2"],
          crossInt["staking.compliance.risk4.bullet3"],
        ]}
        additionalContent={crossInt["staking.compliance.risk4.additionalContent"]}
      />

      <View style={styles.acknowledgement}>
        <Text style={styles.acknowledgementTitle}>
          {crossInt["staking.compliance.acknowledgement.title"]}
        </Text>
        <Text style={styles.acknowledgementText}>
          {crossInt["staking.compliance.acknowledgement"]}
        </Text>
        <View style={styles.agreementContainer}>
          <Text style={styles.agreementText}>
            {crossInt["staking.compliance.agreement.prefix"]}
            <Pressable onPress={() => onAcknowledge && onAcknowledge()}>
              <Text style={styles.linkText}>{crossInt["staking.compliance.agreement"]}</Text>
            </Pressable>

            <Text style={styles.agreementText}>
              {crossInt["staking.compliance.agreement.suffix"]}
            </Text>
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    maxWidth: 480,
    alignSelf: "center",
  },
  mainTitle: {
    ...typography.bold,
    color: colors.whiteOp8,
    marginBottom: 16,
  },
  mainDescription: {
    ...typography.regular,
    color: colors.whiteOp7,
  },
  section: {
    marginTop: 16,
  },
  sectionTitle: {
    ...typography.medium,
    color: colors.whiteOp7,
    marginBottom: 16,
  },
  description: {
    ...typography.regular,
    color: colors.whiteOp7,
    marginBottom: 16,
  },
  bulletPoint: {
    flexDirection: "row",
    marginBottom: 12,
    alignItems: "flex-start",
  },
  bullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.whiteOp7,
    marginTop: 8,
    marginRight: 12,
  },
  bulletText: {
    ...typography.regular,
    color: colors.whiteOp7,
    flex: 1,
  },
  additionalContent: {
    ...typography.regular,
    color: colors.whiteOp7,
  },
  acknowledgement: {
    marginTop: 16,
  },
  acknowledgementTitle: {
    ...typography.bold,
    color: colors.whiteOp8,
    marginBottom: 16,
  },
  acknowledgementText: {
    ...typography.regular,
    color: colors.whiteOp7,
    marginBottom: 16,
  },
  agreementContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  agreementText: {
    ...typography.regular,
    color: colors.white,
  },
  agreementLink: {
    marginHorizontal: 4,
  },
  linkText: {
    ...typography.regular,
    color: colors.orange,
    textDecorationLine: "underline",
  },
});
